import { useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { useQueryClient } from "@tanstack/react-query";

import axiosInstance from "@/api/config";
import { ANALYSIS_CONFIG_MUTATION_KEY } from "@/api/configuration/useConfigurationAPI";
import NotificationContent from "@/components/notification/notificationContent";
import { ALL_COLORS, COLORS } from "@/constants/colors";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import { NPS_MILESTONES } from "@/constants/nps";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { RootState } from "@/store";
import { removeUnOpenedJobId } from "@/store/slice/analysisJob";
import {
  resetGridsSelected,
  setIsHasSummary
} from "@/store/slice/analysisSummary";
import { addDataset, addRawDataset } from "@/store/slice/datasetSlice";
import { clearCurrRunningAnalysisId } from "@/store/slice/spatialSlice";
import {
  setNpsMilestoneReached,
  setUIDataConfig,
  uiInitialState
} from "@/store/slice/uiSlice";
import { clearWidgets } from "@/store/slice/widgetSlice";
import {
  AnalysisStatus,
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetDataRowV2,
  DatasetMetadataStream,
  DatasetStreamDone,
  EColorNames
} from "@/types/dataset";
import {
  applyRestoredLegendStyle,
  resolveLegendAttribute
} from "@/utils/configurationMapper";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import { computeStepValue } from "@/utils/stepValue";

import { useConfiguration } from "../project/useConfiguration";
import useToast, { EToastType } from "../ui/useToast";
import { useAppFeatures } from "../useAppFeatures";
import useEventSource from "../useEventSource";
import {
  type AnalysisLegendStyles,
  useAnalysisLegendConfig
} from "./useAnalysisLegendConfig";

type AnalysisStream = {
  analysis_status: { id: string; status: AnalysisStatus; message?: string };
  "analysis_result:meta": DatasetMetadataStream[];
  "analysis_result:chunk": DatasetChunkStream;
  "analysis_result:done": DatasetStreamDone;
  "dataset_data:chunk": DatasetChunkStream;
  "dataset_data:done": DatasetStreamDone;
  "stream:done": { dataset_id: string };
  "stream:error": void;
};

export const useAnalysisResult = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const chunkBufferRef = useRef<DatasetDataRowV2[]>([]);
  const datasetMetadataRef = useRef<{
    [id: string]: DatasetMetadataStream;
  }>({});
  const datasetDataRef = useRef<{
    [id: string]: { datasetData: DatasetData; newDataset: Dataset };
  }>({});
  const analysisStatusRef = useRef<AnalysisStatus>();
  const analysisMessageRef = useRef<string>();
  const hasHandledAnalysisErrorRef = useRef(false);

  const currRunningAnalysisId = useSelector(
    (state: RootState) => state.spatial.currRunningAnalysisId
  );
  const locallyRunAnalysisId = useSelector(
    (state: RootState) => state.spatial.locallyRunAnalysisId
  );

  const { deleteAllFeatures, map } = useMap();

  const {
    handleClearDataset,
    generateDatasetData,
    handleAddSpatialAnalysisDatasetByType
  } = useDataset();
  const { mutateGetAnalysisConfig } = useConfiguration({});
  const { fetchAnalysisLegendStyles } = useAnalysisLegendConfig();
  const { isDatasetWidgetEnabled } = useAppFeatures();

  const handleAnalysisError = () => {
    if (hasHandledAnalysisErrorRef.current) return;

    hasHandledAnalysisErrorRef.current = true;
    dispatch(clearCurrRunningAnalysisId());
    toast.dismiss("spatial-analysis-toast");

    const errorMessage = analysisMessageRef.current || "";
    const [errorTitle, ...errorDescParts] = errorMessage.split("-");
    const errorDescription = errorDescParts.join("-").trim();
    analysisMessageRef.current = undefined;

    showToast({
      type: EToastType.ERROR,
      message: (
        <NotificationContent
          title={errorTitle?.trim() || "Something Went Wrong"}
          description={
            errorDescription ||
            "We’re sorry, we cannot proceed with your request. Please try again."
          }
        />
      )
    });
    analysisStatusRef.current = undefined;
  };

  useEventSource<AnalysisStream>({
    url: `${axiosInstance.defaults.baseURL}/v1/analysis/${currRunningAnalysisId}/stream`,
    eventKey: ["analysis", { id: currRunningAnalysisId }],
    parse: (data) => JSON.parse(data),
    onOpen: () => {
      hasHandledAnalysisErrorRef.current = false;
      // Entering the analysis-result stream: clear any input geometry (drawn
      // polygons) still on the map so it isn't visible while the result loads.
      // stream:done also clears, but only at the end — this covers the gap.
      deleteAllFeatures();
      dispatch(resetGridsSelected());
      showToast({
        id: "spatial-analysis-toast",
        type: EToastType.LOADING,
        message: (
          <NotificationContent
            title={MESSAGE.LOADING_SPATIAL_ANALYSIS}
            description={MESSAGE.PREPARE_TABULAR_DATA_AND_MAPVIEW}
          />
        )
      });
    },
    eventHandlers: {
      analysis_status: (data) => {
        analysisStatusRef.current = data.status;
        if (data.message) {
          analysisMessageRef.current = data.message;
        }

        if (data.status === AnalysisStatus.FAILED) {
          handleAnalysisError();
        }
      },
      "analysis_result:meta": (data) => {
        data.forEach((dataset) => {
          datasetMetadataRef.current[dataset.dataset_id] = dataset;
        });
      },
      "analysis_result:chunk": (data) => {
        // Avoid spread: `push(...rows)` throws RangeError once a large chunk
        // exceeds the engine's argument limit; useEventSource swallows it.
        for (const row of data.rows) chunkBufferRef.current.push(row);
      },
      "analysis_result:done": (data: DatasetStreamDone) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "dataset_data:chunk": (data) => {
        // Avoid spread: `push(...rows)` throws RangeError once a large chunk
        // exceeds the engine's argument limit; useEventSource swallows it.
        for (const row of data.rows) chunkBufferRef.current.push(row);
      },
      "dataset_data:done": (data) => {
        const dataset = {
          dataset_id: data.dataset_id,
          rows: chunkBufferRef.current,
          seq: data.final_seq
        };

        const { datasetData, newDataset } = generateDatasetData(
          dataset,
          datasetMetadataRef.current[data.dataset_id],
          true
        );

        datasetDataRef.current[data.dataset_id] = {
          datasetData,
          newDataset
        };

        chunkBufferRef.current = [];
      },
      "stream:done": async () => {
        if (analysisStatusRef.current !== AnalysisStatus.SUCCESS) {
          dispatch(clearCurrRunningAnalysisId());
          datasetDataRef.current = {};
          analysisStatusRef.current = undefined;
          analysisMessageRef.current = undefined;

          return;
        }

        // Snapshot before any dispatch that clears this value — used by both
        // mutateGetAnalysisConfig and the fire-and-forget widget fetch below.
        const snapshotAnalysisId = currRunningAnalysisId;

        const legendStylesPromise = snapshotAnalysisId
          ? fetchAnalysisLegendStyles(snapshotAnalysisId)
          : Promise.resolve({} as AnalysisLegendStyles);

        if (snapshotAnalysisId) {
          // Every analysis needs its config restored except the one directly from Analysis Config panel — that config is already in the Redux store.
          // Anything else (job list, history page, finished-analysis toast, a run resumed
          // after a reload) needs to hydrate the analysis config from BE
          const shouldFetchAnalysisConfig =
            snapshotAnalysisId !== locallyRunAnalysisId;

          // Clear current running analysis ID first to immediately disable stream re-subscription
          // (prevents new EventSource connection from opening while waiting for slow config fetch)
          dispatch(clearCurrRunningAnalysisId());

          // Drop it at the start of every run, or the Generate Results
          const mutationCache = queryClient.getMutationCache();
          mutationCache
            .findAll({ mutationKey: ANALYSIS_CONFIG_MUTATION_KEY, exact: true })
            .forEach((mutation) => mutationCache.remove(mutation));

          if (shouldFetchAnalysisConfig) {
            void mutateGetAnalysisConfig({
              analysisId: snapshotAnalysisId
            });
          }
        }

        const finalMessage = analysisMessageRef.current || "";
        const [rawTitle, ...descParts] = finalMessage.split("-");
        const title = rawTitle?.trim() || "";
        const description = descParts.join("-").trim();

        const isHasNoScore = [
          MESSAGE.SPATIAL_ANALYSIS_NO_DATA_AVAILABLE,
          MESSAGE.SPATIAL_ANALYSIS_SCORE_GENERATION_FAILED
        ].includes(title);

        deleteAllFeatures();
        // Result datasets temporarily replace the input datasets. Keep the
        // saved weight baseline so "Edit input" can restore the original
        // advanced-weight validation state before the next dataset change.
        handleClearDataset({ preserveWeightConfigBaseline: true });
        dispatch(
          setUIDataConfig({
            ...uiInitialState,
            showSpatialAnalysisResultTabularModal: false,
            showComparisonPopup: {
              isShow: false,
              isFromAnalysisResult: false,
              isFromAnalysisSummary: false,
              isLimitShow: false
            },
            sideBarMenuTypePopupDisplay: null,
            showCatchmentPopup: false,
            showAnalysisResultPopup: true,
            showAnalysisSummaryPopup: isDatasetWidgetEnabled,
            sidebarUserSettingPopupDisplay: {
              isPanelView: false,
              isPanelAdd: false,
              isPanelEdit: false
            }
          })
        );
        toast.dismiss("spatial-analysis-toast");
        dispatch(removeUnOpenedJobId(snapshotAnalysisId ?? ""));
        if (isDatasetWidgetEnabled) {
          dispatch(clearWidgets());
        }

        // Clear the ref before awaiting so a repeat `stream:done` can't double-add.
        const entries = Object.entries(datasetDataRef.current);
        datasetDataRef.current = {};
        const isAnalysisResultEntry = ([, data]: (typeof entries)[number]) =>
          data.datasetData.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;

        // The "spatial analysis result" dataset must be processed last so its
        // layer renders on top of the others. Keep the remaining datasets
        // reversed (newest first) and append the analysis result at the end.
        const datasetDataList = [
          ...entries.filter((entry) => !isAnalysisResultEntry(entry)).reverse(),
          ...entries.filter(isAnalysisResultEntry)
        ];

        const legendStyles = await legendStylesPromise;

        for (const [index, [datasetId, data]] of datasetDataList.entries()) {
          const { datasetData, newDataset } = data;
          const featureCollection =
            convertDatasetResponseToFeatureCollection(datasetData);
          const restored = legendStyles[datasetId];
          // The layer is built from `selectedCategory`, so the legend has to come
          // back with its column or its steps match no feature.
          const restoredCategory = resolveLegendAttribute(
            newDataset.categories,
            restored?.colorBased
          );
          const restoredDataset = restoredCategory
            ? { ...newDataset, selectedCategory: restoredCategory }
            : newDataset;

          dispatch(addRawDataset({ dataset: datasetData, id: datasetId }));
          dispatch(addDataset({ dataset: restoredDataset }));

          // Loop through colors if there are more datasets than available colors
          const loopIndex = index % ALL_COLORS.length;
          const colorName = ALL_COLORS[loopIndex] as keyof typeof EColorNames;

          handleAddSpatialAnalysisDatasetByType({
            spatialType: datasetData.spatial_aggregation_type,
            newDataset: {
              ...restoredDataset,
              // A restored legend wins over the generated palette.
              style: applyRestoredLegendStyle(
                {
                  ...restoredDataset.style,
                  fillColor: COLORS[colorName][500],
                  colorFamily: EColorNames[colorName],
                  colorSteps: computeStepValue(datasetData.rows.length)
                },
                restored?.style
              )
            },
            featureCollection
          });
        }

        dispatch(setIsHasSummary(!isHasNoScore));
        dispatch(
          setNpsMilestoneReached(NPS_MILESTONES.COMPLETED_FIRST_ANALYSIS)
        );

        analysisStatusRef.current = undefined;

        analysisMessageRef.current = undefined;

        showToast({
          type: isHasNoScore ? EToastType.WARNING : EToastType.SUCCESS,
          message: (
            <NotificationContent
              title={
                title
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_TITLE
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_TITLE
              }
              description={
                description
                  ? MESSAGE.SPATIAL_ANALYSIS_SCORE_NOT_AVAILABLE_DESCRIPTION
                  : MESSAGE.SPATIAL_ANALYSIS_SUCCESS_DESCRIPTION
              }
            />
          )
        });
      },
      "stream:error": handleAnalysisError
    },
    enabled: !!currRunningAnalysisId && map?._loaded
  });
};

export default useAnalysisResult;
